import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faExclamationTriangle,
  faHourglassHalf,
  faSearch,
  faFilter,
  faInfoCircle,
} from "@fortawesome/free-solid-svg-icons";

const ValidationProcessTable = ({
  data,
  isInValidationProcess,
  isOutOfTimeValidation,
  formatDate,
  onItemClick,
}) => {
  const [showAllReferences, setShowAllReferences] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  if (!data || data.length === 0) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-800 flex items-center">
            <FontAwesomeIcon
              icon={faHourglassHalf}
              className="mr-2 text-orange-600"
            />
            Validation Process
          </h3>
        </div>
        <div className="flex-grow flex flex-col items-center justify-center text-gray-500">
          <FontAwesomeIcon icon={faHourglassHalf} className="text-4xl mb-2" />
          <p>No references in validation process</p>
        </div>
      </div>
    );
  }

  const validationData = data.filter(
    (item) => isInValidationProcess(item) || isOutOfTimeValidation(item)
  );

  const outOfTimeData = data.filter((item) => isOutOfTimeValidation(item));

  // Filter by search term
  const filteredData = validationData.filter((item) => {
    if (!searchTerm) return true;
    const reference = item.header.reference?.toLowerCase() || "";
    const customer = item.header.customer_name?.toLowerCase() || "";
    return (
      reference.includes(searchTerm.toLowerCase()) ||
      customer.includes(searchTerm.toLowerCase())
    );
  });

  // Sort data - out of time first, then by date
  const sortedData = [...filteredData].sort((a, b) => {
    // First sort by out of time status
    const aOutOfTime = isOutOfTimeValidation(a);
    const bOutOfTime = isOutOfTimeValidation(b);
    if (aOutOfTime && !bOutOfTime) return -1;
    if (!aOutOfTime && bOutOfTime) return 1;

    // Then sort by alta date
    const aDate = a.header.ebookingDates?.fechaAlta || "";
    const bDate = b.header.ebookingDates?.fechaAlta || "";
    return aDate < bDate ? -1 : aDate > bDate ? 1 : 0;
  });

  // Limit the number of references shown
  const displayData = showAllReferences ? sortedData : sortedData.slice(0, 5);

  return (
    <div className="flex flex-col h-full">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-800 flex items-center">
          <FontAwesomeIcon
            icon={faHourglassHalf}
            className="mr-2 text-orange-600"
          />
          Validation Process
        </h3>
        <div className="text-xs text-red-600 flex items-center">
          {outOfTimeData.length > 0 && (
            <>
              <FontAwesomeIcon icon={faExclamationTriangle} className="mr-1" />
              {outOfTimeData.length} out of time
            </>
          )}
        </div>
      </div>

      {/* Search input */}
      <div className="relative mb-3">
        <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
          <FontAwesomeIcon icon={faSearch} className="text-gray-400 text-xs" />
        </div>
        <input
          type="text"
          className="w-full pl-7 pr-2 py-1 text-xs border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          placeholder="Search references..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <div className="flex-grow overflow-auto">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th
                scope="col"
                className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Reference
              </th>
              <th
                scope="col"
                className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Alta Date
              </th>
              <th
                scope="col"
                className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Status
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {displayData.length === 0 ? (
              <tr>
                <td
                  colSpan="3"
                  className="px-2 py-4 text-center text-sm text-gray-500"
                >
                  No matching references found
                </td>
              </tr>
            ) : (
              displayData.map((item, index) => (
                <tr
                  key={item.header.reference || index}
                  className={`${
                    isOutOfTimeValidation(item)
                      ? "bg-red-50"
                      : "hover:bg-gray-50"
                  } cursor-pointer transition-colors`}
                  onClick={() => onItemClick(item)}
                >
                  <td className="px-2 py-2 whitespace-nowrap">
                    <div className="font-medium text-gray-900 flex items-center">
                      {item.header.reference}
                      {isOutOfTimeValidation(item) && (
                        <FontAwesomeIcon
                          icon={faExclamationTriangle}
                          className="ml-1 text-red-500 text-xs"
                          title="Out of time"
                        />
                      )}
                    </div>
                    <div className="text-xs text-gray-500">
                      {item.header.customer_name}
                    </div>
                  </td>
                  <td className="px-2 py-2 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {formatDate(item.header.ebookingDates?.fechaAlta)}
                    </div>
                  </td>
                  <td className="px-2 py-2 whitespace-nowrap">
                    <span
                      className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        isOutOfTimeValidation(item)
                          ? "bg-red-100 text-red-800"
                          : "bg-yellow-100 text-yellow-800"
                      }`}
                    >
                      {isOutOfTimeValidation(item)
                        ? "Out of Time"
                        : "In Process"}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {sortedData.length > 5 && (
        <div className="mt-3 text-center">
          <button
            onClick={() => setShowAllReferences(!showAllReferences)}
            className="text-xs text-blue-600 hover:text-blue-800 transition-colors"
          >
            {showAllReferences
              ? "Show Less"
              : `Show All (${sortedData.length})`}
          </button>
        </div>
      )}

      <div className="mt-3 pt-2 border-t border-gray-200 flex justify-between items-center text-xs text-gray-500">
        <div className="flex items-center">
          <FontAwesomeIcon icon={faInfoCircle} className="mr-1" />
          <span>References in validation process</span>
        </div>
        <div className="font-medium">Total: {validationData.length}</div>
      </div>

      {/* View All Button for Mobile/Small Screens */}
      {!showAllReferences && sortedData.length > 5 && (
        <button
          className="w-full mt-3 bg-blue-50 text-blue-700 hover:bg-blue-100 py-2 rounded-md text-xs font-medium transition-colors"
          onClick={() => setShowAllReferences(true)}
        >
          View All References ({sortedData.length})
        </button>
      )}
    </div>
  );
};

export default ValidationProcessTable;
